import { Injectable, OnDestroy } from '@angular/core';
import { ManagementQueueResult } from '@Models';
import { Observable } from 'rxjs';
import { computeSortedIndexes, CaseSortWorkerRequest, CaseSortWorkerResponse } from './case-sort.utils';

/**
 * Offloads sorting the (potentially 7,000+ row) case list to a Web Worker so the
 * main thread / UI doesn't freeze while computing sort keys (date parsing, etc.)
 * and running the comparison sort. Falls back to sorting synchronously on the
 * main thread when Web Workers aren't available (e.g. server-side rendering).
 */
@Injectable()
export class CaseSortWorkerService implements OnDestroy {
    private worker: Worker;
    private nextRequestId = 0;

    sort(
        rows: ManagementQueueResult[],
        sortActive: string,
        sortDirection: 'asc' | 'desc' | '',
        columnKeyMap: Record<string, string>,
        useStrictValidation: boolean
    ): Observable<number[]> {
        if (typeof Worker === 'undefined') {
            return new Observable<number[]>(subscriber => {
                subscriber.next(computeSortedIndexes(rows, sortActive, sortDirection, columnKeyMap, useStrictValidation));
                subscriber.complete();
            });
        }

        const requestId = this.nextRequestId++;

        return new Observable<number[]>(subscriber => {
            const worker = this.getOrCreateWorker();

            const onMessage = ({ data }: MessageEvent<CaseSortWorkerResponse>) => {
                if (data.requestId !== requestId) {
                    return;
                }

                worker.removeEventListener('message', onMessage);
                worker.removeEventListener('error', onError);
                subscriber.next(data.sortedIndexes);
                subscriber.complete();
            };

            const onError = (error: ErrorEvent) => {
                worker.removeEventListener('message', onMessage);
                worker.removeEventListener('error', onError);
                subscriber.error(error);
            };

            worker.addEventListener('message', onMessage);
            worker.addEventListener('error', onError);

            const request: CaseSortWorkerRequest = { requestId, rows, sortActive, sortDirection, columnKeyMap, useStrictValidation };
            worker.postMessage(request);

            return () => {
                worker.removeEventListener('message', onMessage);
                worker.removeEventListener('error', onError);
            };
        });
    }

    ngOnDestroy(): void {
        this.terminate();
    }

    terminate(): void {
        this.worker?.terminate();
        this.worker = undefined;
    }

    private getOrCreateWorker(): Worker {
        if (!this.worker) {
            this.worker = new Worker(new URL('./case-sort.worker', import.meta.url));
        }

        return this.worker;
    }
}
